import React, { useCallback } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { BranchDto } from '@api/nodeDefinitions';
import type { GateDecisionValue } from '@api/processRuns';
import type { Port } from '../graphTypes';
import { resolveIcon } from '../sidebar/iconRegistry';
import { useEditorContext } from '../useEditorContext';
import {
  BranchHandlePill,
  BranchIconWrap,
  BranchLabelText,
  BranchSlot,
} from './BranchRow.styled';

export interface BranchRowProps {
  readonly branches: readonly BranchDto[];
  readonly ports: readonly Port[] | undefined;
  readonly nodeId: string;
}

/**
 * Maps a branch tone ("approved" / "rejected") to the matching
 * {@link GateDecisionValue}. Anything else (a future neutral branch,
 * a misconfigured catalog entry) is intentionally ignored so we don't
 * trigger spurious gate decisions.
 */
const decisionForTone = (tone: string): GateDecisionValue | null => {
  if (tone === 'approved') return 'approved';
  if (tone === 'rejected') return 'rejected';
  return null;
};

const CHEVRON_SVG = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Renders the branched-output column beneath the gate node body. Each
 * row shows a tinted icon, a label, and a connection pill at the card's
 * right edge — matching the design's {@code .ns-edit--gate__decisions}.
 *
 * <p>Each branch's {@code <Handle>} is wrapped in a
 * {@code <BranchHandlePill>} — a capsule pill with a chevron that
 * matches the output-pill design from {@code PortHandles}. The pill is
 * absolutely positioned so it straddles the card's right border.</p>
 */
const BranchRow: React.FC<BranchRowProps> = ({ branches, ports, nodeId }) => {
  const { getGateWaitingForNode, requestGateDecision } = useEditorContext();
  // Only the currently-waiting gate node turns its branches into real
  // controls. In edit/draft mode (or while the gate is idle) the row
  // is purely decorative and clicks are no-ops — matches the user's
  // expectation that "Approved / Rejected" pills are clickable only
  // when a run is actively waiting on this node.
  const gateWaiting = getGateWaitingForNode(nodeId);
  const interactive = gateWaiting != null;

  const onActivate = useCallback(
    (decision: GateDecisionValue, e: React.SyntheticEvent) => {
      // Stop React Flow from treating the click as a node-select drag.
      e.stopPropagation();
      requestGateDecision(nodeId, decision);
    },
    [nodeId, requestGateDecision],
  );

  if (branches.length === 0) return null;
  return (
    <>
      {branches.map((branch) => {
        const port = findOutputPortByLabel(ports, branch.portLabel);
        const Icon = resolveIcon(branch.iconId);
        const slug = branch.portLabel.toLowerCase();
        const tone = branch.tone as 'approved' | 'rejected';
        const decision = interactive ? decisionForTone(branch.tone) : null;

        const handleClick = decision
          ? (e: React.MouseEvent) => onActivate(decision, e)
          : undefined;
        const handleKeyDown = decision
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onActivate(decision, e);
              }
            }
          : undefined;

        return (
          <BranchSlot
            key={branch.portLabel}
            data-tone={tone}
            data-testid={`process-node__branch-${slug}-${nodeId}`}
            $interactive={Boolean(decision)}
            role={decision ? 'button' : undefined}
            tabIndex={decision ? 0 : undefined}
            aria-label={
              decision ? `${branch.portLabel} this gate` : undefined
            }
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={decision ? 'nodrag' : undefined}
          >
            <BranchIconWrap $tone={tone} aria-hidden="true">
              <Icon />
            </BranchIconWrap>
            <BranchLabelText>{branch.portLabel}</BranchLabelText>
            {port ? (
              <BranchHandlePill>
                {CHEVRON_SVG}
                <Handle
                  type="source"
                  position={Position.Right}
                  id={port.portId}
                  data-testid={`process-node__handle-${slug}-${nodeId}`}
                />
              </BranchHandlePill>
            ) : null}
          </BranchSlot>
        );
      })}
    </>
  );
};

const findOutputPortByLabel = (
  ports: readonly Port[] | undefined,
  label: string,
): Port | undefined => {
  if (!Array.isArray(ports)) return undefined;
  const lower = label.toLowerCase();
  return ports.find(
    (p) => p.direction === 'output' && p.label.toLowerCase() === lower,
  );
};

export default BranchRow;
